import React from "react";
import { Shirt, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RPG_TEMPLATES = ["rpg3d", "explorer3d", "collector3d", "platformer3d", "fps3d", "battle3d", "horror3d", "runner3d", "shooter3d"];
const STATS = [
  { v: "damage", label: "⚔️ Attack" },
  { v: "speed", label: "⚡ Move speed" },
  { v: "jump", label: "🦘 Jump" },
  { v: "coins", label: "🪙 Coins" },
  { v: "maxHp", label: "❤️ Max health" },
];
const RARITIES = [
  { v: "uncommon", label: "Uncommon+" },
  { v: "rare", label: "Rare+" },
  { v: "epic", label: "Epic+" },
  { v: "legendary", label: "Legendary" },
];
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 24);

// Hand-editable gear-set bonuses. Own all pieces → a passive perk. Writes config.setBonuses.
export function SetBonusPanel({ config, setField, is3D }) {
  const tpl = config?.template || "";
  const sets = Array.isArray(config?.setBonuses) ? config.setBonuses : [];
  const show = is3D && (RPG_TEMPLATES.includes(tpl) || sets.length > 0);
  if (!show) return null;

  // known item ids to help the creator wire pieces
  const known = [];
  (Array.isArray(config?.items) ? config.items : []).forEach((it) => it && known.push(slug(it.id || it.name)));
  (config?.shop?.items || []).forEach((it) => it && known.push(slug(it.id || it.name)));
  (Array.isArray(config?.crafting) ? config.crafting : []).forEach((r) => r?.output && known.push(slug(r.output.id || r.output.name)));
  const ids = [...new Set(known.filter(Boolean))];

  const write = (arr) => setField("setBonuses", arr);
  const update = (i, patch) => write(sets.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const add = () =>
    write([...sets, { id: "set" + (sets.length + 1), name: "New Set", icon: "🧰", pieces: ids.slice(0, 2), effect: { stat: "damage", value: 0.2 } }]);
  const remove = (i) => write(sets.filter((_, k) => k !== i));
  const isHp = (s) => (s.effect?.stat || "damage") === "maxHp";

  return (
    <div className="pt-2 border-t border-[hsl(var(--border))] space-y-3" data-testid="set-bonus-panel">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-bold flex items-center gap-1.5"><Shirt className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Set Bonuses</div>
        <span className="ml-auto text-[10px] text-[hsl(var(--muted-foreground))]">own all pieces → passive perk</span>
      </div>
      {sets.length === 0 && (
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Reward players for collecting a matching gear set (e.g. helm + armor + boots).</p>
      )}
      <div className="space-y-2" data-testid="set-bonus-list">
        {sets.map((s, i) => (
          <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-2 space-y-1.5" data-testid={`set-bonus-${i}`}>
            <div className="flex items-center gap-1.5">
              <Input value={s.icon || "🧰"} onChange={(e) => update(i, { icon: e.target.value.slice(0, 4) })} className="h-7 w-9 text-center px-0" data-testid={`set-icon-${i}`} />
              <Input value={s.name || ""} onChange={(e) => update(i, { name: e.target.value, id: s.id || slug(e.target.value) })} placeholder="Set name" className="h-7 flex-1 min-w-0 px-1.5" data-testid={`set-name-${i}`} />
              <button type="button" onClick={() => remove(i)} className="h-7 w-6 grid place-items-center rounded-lg border border-[hsl(var(--border))] hover:border-rose-500/60 text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`set-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
            </div>
            <Input
              value={(s.pieces || []).join(", ")}
              onChange={(e) => update(i, { pieces: e.target.value.split(",").map((p) => slug(p)).filter(Boolean) })}
              placeholder="piece item ids, comma-separated (e.g. helm, armor, boots)"
              className="h-7 px-1.5 text-[11px]"
              data-testid={`set-pieces-${i}`}
              list={`set-ids-${i}`}
            />
            <datalist id={`set-ids-${i}`}>{ids.map((id) => <option key={id} value={id} />)}</datalist>
            <div className="grid grid-cols-[1fr_58px] gap-1 items-center">
              <select value={s.effect?.stat || "damage"} onChange={(e) => update(i, { effect: { ...(s.effect || {}), stat: e.target.value } })} className="h-7 rounded-lg border border-[hsl(var(--border))] bg-transparent text-[11px] px-1" data-testid={`set-stat-${i}`}>
                {STATS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <Input
                type="number"
                step={isHp(s) ? 1 : 0.05}
                min={isHp(s) ? 1 : 0.05}
                max={isHp(s) ? 5 : 2}
                value={s.effect?.value ?? (isHp(s) ? 1 : 0.2)}
                onChange={(e) => update(i, { effect: { ...(s.effect || {}), value: +e.target.value || 0 } })}
                className="h-7 text-center px-0.5"
                data-testid={`set-value-${i}`}
                title={isHp(s) ? "extra hearts" : "fraction (0.2 = +20%)"}
              />
            </div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{isHp(s) ? "value = extra hearts" : "value = bonus fraction (0.2 = +20%)"}</div>
            {s.upgrade ? (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-1.5 space-y-1" data-testid={`set-elite-${i}`}>
                <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-500">⭐ ELITE upgrade
                  <button type="button" onClick={() => update(i, { upgrade: null })} className="ml-auto text-[hsl(var(--muted-foreground))] hover:text-rose-500" data-testid={`set-elite-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Stronger bonus when every piece is this rarity or higher:</div>
                <div className="grid grid-cols-[1fr_1fr_50px] gap-1 items-center">
                  <select value={s.upgrade.rarity || "rare"} onChange={(e) => update(i, { upgrade: { ...s.upgrade, rarity: e.target.value } })} className="h-7 rounded-lg border border-[hsl(var(--border))] bg-transparent text-[11px] px-1" data-testid={`set-elite-rarity-${i}`}>
                    {RARITIES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                  <select value={s.upgrade.effect?.stat || "damage"} onChange={(e) => update(i, { upgrade: { ...s.upgrade, effect: { ...(s.upgrade.effect || {}), stat: e.target.value } } })} className="h-7 rounded-lg border border-[hsl(var(--border))] bg-transparent text-[11px] px-1" data-testid={`set-elite-stat-${i}`}>
                    {STATS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                  <Input type="number" step={(s.upgrade.effect?.stat === "maxHp") ? 1 : 0.05} value={s.upgrade.effect?.value ?? 0.5} onChange={(e) => update(i, { upgrade: { ...s.upgrade, effect: { ...(s.upgrade.effect || {}), value: +e.target.value || 0 } } })} className="h-7 text-center px-0.5" data-testid={`set-elite-value-${i}`} />
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => update(i, { upgrade: { rarity: "rare", effect: { stat: s.effect?.stat || "damage", value: Math.min((s.effect?.stat === "maxHp") ? 6 : 3, (s.effect?.value || 0.2) * 2) } } })} className="text-[10px] font-semibold text-amber-500/90 hover:text-amber-400" data-testid={`set-add-elite-${i}`}>⭐ Add Elite tier</button>
            )}
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={add} className="h-7 text-[11px]" data-testid="add-set-bonus-btn"><Plus className="w-3 h-3" /> Add set bonus</Button>
    </div>
  );
}
